/**
 * D7B - the consolidated invoice as the client receives it.
 *
 * The bill is always to the CLIENT (FRD §8); the room-wise breakup is
 * supporting detail inside the document, never a separate bill.
 *
 * THIS PAGE IS A VIEWER, NOT A RENDERER. The document comes from
 * backend/packages/invoice over GET /admin/b2b/invoices/:id/pdf and is embedded
 * as-is, so what an operator sees, downloads and prints are the same bytes the
 * client is sent - see docs/INVOICE_DESIGN.md. An earlier revision hand-built
 * the sheet in React, which made this the second invoice renderer in the system
 * and failed `scripts/check-invoice-boundary.js`. Do not reintroduce invoice
 * markup or invoice palette values here.
 */
import { useCallback, useEffect, useState } from 'react'
import { ArrowLeft, Download, Printer } from 'lucide-react'
import { useNavigate, useParams } from 'react-router-dom'

import EmptyState from '../../components/shared/EmptyState'
import LoadErrorState from '../../components/common/LoadErrorState'
import { withServerTimeout } from '../../components/common/loadErrorUtils'
import {
  Card,
  CardTitle,
  Chip,
  PageHeader,
  PillButton,
  StatCard,
  StatRow,
} from '../../features/b2b/components/primitives'
import InvoiceDocumentFrame from '../../features/b2b/components/InvoiceDocumentFrame'
import { useInvoiceDocument } from '../../features/b2b/useInvoiceDocument'
import { formatDate, formatMoney } from '../../features/b2b/format'
import { b2bErrorMessage } from '../../features/b2b/errorCopy'
import { b2bApi } from '../../services/b2bApi'
import type { B2bInvoiceDetail } from '../../services/b2bApi'

const PAYMENT_TONE = {
  paid: 'success',
  partly_paid: 'warning',
  unpaid: 'info',
} as const

const PAYMENT_LABEL = {
  paid: 'Paid',
  partly_paid: 'Partly paid',
  unpaid: 'Unpaid',
} as const

export default function B2bInvoicePreviewPage() {
  const { invoiceId } = useParams<{ invoiceId: string }>()
  const navigate = useNavigate()

  const [detail, setDetail] = useState<B2bInvoiceDetail | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')

  const load = useCallback(async () => {
    if (!invoiceId) {
      setLoading(false)
      return
    }
    setLoading(true)
    setError('')
    try {
      setDetail(await withServerTimeout(b2bApi.getInvoice(invoiceId)))
    } catch (err) {
      setError(b2bErrorMessage(err, 'loading this invoice'))
    } finally {
      setLoading(false)
    }
  }, [invoiceId])

  useEffect(() => {
    void load()
  }, [load])

  // The rendered document itself - fetched once, used by the frame, the
  // download and the print job.
  const document = useInvoiceDocument(invoiceId)

  if (error) {
    return (
      <div>
        <PageHeader title="Invoice" />
        <LoadErrorState message={error} onRetry={() => void load()} />
      </div>
    )
  }

  if (!loading && !detail) {
    return (
      <div>
        <PageHeader title="Invoice not found" />
        <Card section>
          <EmptyState title="Nothing to show" description="This invoice no longer exists." />
          <div style={{ display: 'flex', justifyContent: 'center' }}>
            <PillButton onClick={() => navigate('/b2b/invoices')}>Back to Invoices</PillButton>
          </div>
        </Card>
      </div>
    )
  }

  const invoice = detail?.invoice
  const client = detail?.client
  const cycle = detail?.cycle
  const status = invoice?.payment_status ?? 'unpaid'

  return (
    <div>
      <PageHeader
        title={invoice?.invoice_number ?? 'Invoice'}
        subtitle={client && cycle ? `${client.name} · ${cycle.label}` : undefined}
        actions={
          <>
            <PillButton variant="secondary" onClick={() => navigate(-1)}>
              <ArrowLeft size={15} /> Back
            </PillButton>
            <PillButton
              variant="secondary"
              onClick={document.print}
              disabled={!document.url}
              title={document.url ? undefined : 'The document is still loading.'}
            >
              <Printer size={15} /> Print
            </PillButton>
            <PillButton
              onClick={document.download}
              disabled={!document.url}
              title={document.url ? undefined : 'The document is still loading.'}
            >
              <Download size={15} /> Download PDF
            </PillButton>
          </>
        }
      />

      <StatRow>
        <StatCard label="Billed to" value={client?.name ?? '-'} hint={client?.address} />
        <StatCard
          label="Billing cycle"
          value={cycle?.label ?? '-'}
          hint={invoice ? `Issued ${formatDate(invoice.generated_at)}` : undefined}
        />
        <StatCard label="Rooms billed" value={String(invoice?.rooms_billed ?? 0)} />
        <StatCard label="Invoice total" value={formatMoney(invoice?.total_amount ?? 0)} />
      </StatRow>

      <Card section>
        <CardTitle
          right={
            <Chip tone={PAYMENT_TONE[status] ?? 'info'}>{PAYMENT_LABEL[status] ?? status}</Chip>
          }
        >
          Invoice document
        </CardTitle>

        <InvoiceDocumentFrame
          src={document.url}
          title={`Invoice ${invoice?.invoice_number ?? ''} for ${client?.name ?? 'client'}`}
          emptyTitle={document.loading ? 'Preparing the document' : 'Document not available'}
          emptyDescription={
            document.loading
              ? 'Rapidry is fetching the invoice from the server so the copy you see is the copy the client receives.'
              : document.error ||
                'Rapidry prepares the invoice on the server so the copy you see is the copy the client receives. It could not be loaded.'
          }
          action={
            !document.loading && document.error ? (
              <PillButton onClick={document.reload}>Try again</PillButton>
            ) : null
          }
        />
      </Card>

      {detail && detail.lines.length > 0 ? (
        <Card section style={{ marginTop: '18px' }}>
          <CardTitle>Room-wise breakup</CardTitle>
          <p className="font-body" style={{ margin: '0 0 12px', fontSize: '12px', color: 'var(--text-muted)' }}>
            Supporting detail only - the amount due is billed at the client level (FRD §8). These
            are the rooms that had laundry; the rest are excluded entirely.
          </p>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
            {detail.lines.map((line, index) => (
              <Chip key={`${line.room_number}-${line.service_slug}-${index}`}>
                {line.room_number} · {line.service_label} · {formatMoney(line.amount)}
              </Chip>
            ))}
          </div>
        </Card>
      ) : null}
    </div>
  )
}
